import {
  CardsBold,
  CardsOutline as Cards,
  CompanyType,
  CupBold,
  CupOutline,
  DocumentBold,
  DocumentDownloadOutline as DocumentDownload,
  DocumentOutline as Document,
  GridFiveBold,
  GridFiveOutline,
  HomeBold,
  HomeOutline as Home,
  HeartBold,
  HeartOutline,
  ManageBold,
  ManageOutline,
  NavItem,
  NavSection,
  PeopleBold,
  PeopleOutline,
  Profile2UserBold,
  Profile2UserOutline,
  SettingsBold,
  SettingsOutline,
  TaskBold,
  TaskOutline,
  UserOctagonBold,
  UserOctagonOutline,
  WalletBold,
  WalletOutline,
} from '@aslanpay/portal-common-lib';
import { useLocation } from 'react-router-dom';

import { getBlankPath } from 'app/utils/routes';
import { useMemo } from 'react';

const ICON_SIZE = '20px';
const blankPath = getBlankPath();

const icons = {
  cards: {
    default: <Cards boxSize={ICON_SIZE} />,
    active: <CardsBold boxSize={ICON_SIZE} />,
  },
  cup: {
    default: <CupOutline boxSize={ICON_SIZE} />,
    active: <CupBold boxSize={ICON_SIZE} />,
  },
  document: {
    default: <DocumentDownload boxSize={ICON_SIZE} />,
    active: <DocumentDownload boxSize={ICON_SIZE} />,
  },
  audit: {
    default: <Document boxSize={ICON_SIZE} />,
    active: <DocumentBold boxSize={ICON_SIZE} />,
  },
  grid: {
    default: <GridFiveOutline boxSize={ICON_SIZE} />,
    active: <GridFiveBold boxSize={ICON_SIZE} />,
  },
  home: {
    default: <Home boxSize={ICON_SIZE} />,
    active: <HomeBold boxSize={ICON_SIZE} />,
  },
  people: {
    default: <PeopleOutline boxSize={ICON_SIZE} />,
    active: <PeopleBold boxSize={ICON_SIZE} />,
  },
  settings: {
    default: <SettingsOutline boxSize={ICON_SIZE} />,
    active: <SettingsBold boxSize={ICON_SIZE} />,
  },
  task: {
    default: <TaskOutline boxSize={ICON_SIZE} />,
    active: <TaskBold boxSize={ICON_SIZE} />,
  },
  manage: {
    default: <ManageOutline boxSize={ICON_SIZE} />,
    active: <ManageBold boxSize={ICON_SIZE} />,
  },
  user: {
    default: <Profile2UserOutline boxSize={ICON_SIZE} />,
    active: <Profile2UserBold boxSize={ICON_SIZE} />,
  },
  userOctagon: {
    default: <UserOctagonOutline boxSize={ICON_SIZE} />,
    active: <UserOctagonBold boxSize={ICON_SIZE} />,
  },
  wallet: {
    default: <WalletOutline boxSize={ICON_SIZE} />,
    active: <WalletBold boxSize={ICON_SIZE} />,
  },
  heart: {
    default: <HeartOutline boxSize={ICON_SIZE} />,
    active: <HeartBold boxSize={ICON_SIZE} />,
  },
};

const createItem = (
  id: string,
  label: string,
  path: string,
  icon: { default: JSX.Element; active?: JSX.Element }
) => ({
  id,
  label,
  path,
  icon: icon.default,
  iconActive: icon.active,
});

function getMyAslanItems(isRewardAndRecognitionEnabled: boolean) {
  const walletLabel = isRewardAndRecognitionEnabled ? 'Wallets & Pots' : 'Wallets';
  const baseItems = [
    createItem('home', 'Home', blankPath, icons.home),
    createItem('employees', 'Employees', '/employees', icons.user),
    createItem('walletsAndPots', walletLabel, '/wallets-and-pots', icons.wallet),
  ];

  baseItems.push(createItem('admins', 'Admins', '/admins', icons.userOctagon));
  baseItems.push(createItem('reports', 'Reports', '/reports', icons.document));

  return baseItems;
}

const rewardAndRecognitionSection: NavSection = {
  id: 'reward-recognition',
  label: 'Reward & Recognition',
  defaultExpanded: false,
  items: [
    createItem(
      'reward-and-recognition-overview',
      'Recognition overview',
      '/reward-and-recognition',
      icons.grid
    ),
    createItem('my-prides', 'My Prides', '/reward-and-recognition/my-prides', icons.people),
    createItem('the-roar', 'The Roar', '/reward-and-recognition/the-roar', icons.cup),
    createItem('approvals', 'Approvals', '/reward-and-recognition/approvals', icons.task),
    createItem('recognition-audit', 'Audit', '/reward-and-recognition/audit', icons.audit),
  ],
};

const companyBenefitsSection: NavSection = {
  id: 'company-benefits',
  label: 'Company Benefits',
  hidden: false,
  defaultExpanded: false,
  items: [
    createItem('benefits-overview', 'Benefits overview', '/benefits', icons.grid),
    createItem('benefits-manager', 'Benefits Manager', '/benefits/manager', icons.manage),
    // createItem('categories', 'Categories', '/benefits/categories', icons.home),
    // createItem('communications', 'Communications', '/benefits/communications', icons.home),
  ],
};

// The employee portal is a flat, ungrouped list: every destination is a top-level
// item, so nothing is hidden behind a disclosure triangle. Adding a page is a single
// `createItem` line below. Only reintroduce a labelled NavSection once at least two
// items belong under the same heading.
function getEmployeeSections(
  isRewardAndRecognitionEnabled: boolean,
  isBenefitHostingEnabled: boolean,
  isPotManager: boolean
): NavSection[] {
  const items: NavItem[] = [];

  if (isRewardAndRecognitionEnabled) {
    items.push(createItem('the-roar', 'The Roar', '/reward-and-recognition/the-roar', icons.cup));

    if (isPotManager) {
      items.push(
        createItem('approvals', 'Approvals', '/reward-and-recognition/approvals', icons.task)
      );
    }
  }

  if (isBenefitHostingEnabled) {
    items.push(createItem('benefits-overview', 'Benefits', '/benefits', icons.heart));
  }

  if (items.length === 0) {
    return [];
  }

  // No `label`: a section without one renders its items flat, with no header,
  // chevron or collapse behaviour.
  return [{ id: 'employee-nav', items }];
}

export function buildNavSections(
  companyType: CompanyType,
  isGiftingEnabled: boolean,
  isRewardAndRecognitionEnabled: boolean,
  isBenefitHostingEnabled: boolean,
  isEmployeeContext = false,
  isPotManager = false
): NavSection[] {
  if (isEmployeeContext) {
    return getEmployeeSections(
      isRewardAndRecognitionEnabled,
      isBenefitHostingEnabled,
      isPotManager
    );
  }

  const myAslanItems = [...getMyAslanItems(isRewardAndRecognitionEnabled)];

  if (isGiftingEnabled) {
    myAslanItems.push(createItem('gifting', 'Gifting', '/gifting', icons.cards));
  }

  const sections: NavSection[] = [
    {
      id: 'my-aslan',
      label: 'My Aslan',
      defaultExpanded: false,
      items: myAslanItems,
    },
  ];

  if (isRewardAndRecognitionEnabled) {
    sections.push(rewardAndRecognitionSection);
  }

  if (isBenefitHostingEnabled) {
    sections.push(companyBenefitsSection);
  }

  return sections;
}

export function useActiveNavId(sections: NavSection[]): string {
  const { pathname, state } = useLocation();
  const navState = state as { activeNavId?: string } | null;

  const activeId = useMemo(() => {
    if (navState?.activeNavId) {
      return navState.activeNavId;
    }

    // Benefits-specific rule: Overview owns only `/benefits` exactly. Any deeper
    // `/benefits/*` path (manager, new, :id, :id/edit) belongs to Benefits Manager.
    if (pathname === '/benefits') {
      return 'benefits-overview';
    }
    if (
      pathname.startsWith('/benefits/') &&
      sections.some((section) => section.items.some((item) => item.id === 'benefits-manager'))
    ) {
      return 'benefits-manager';
    }

    const sortedItems = sections
      .flatMap((section) => section.items)
      .sort((a, b) => b.path.length - a.path.length);

    const match = sortedItems.find((item) =>
      item.path === '/' || item.path === blankPath
        ? pathname === item.path
        : pathname.startsWith(item.path)
    );

    return match?.id ?? 'home';
  }, [sections, pathname, navState?.activeNavId]);

  return activeId;
}
